import { api } from "../../api";

const createQueryParams = (params, isAdditionalFilters) => {
    if (!params || typeof params !== "object") return "";

    const appliedFilters = Object.keys(params)
        .map((key) => {
            const value = params[key];
            if (value === undefined || value === null || value === "")
                return "";
            return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        })
        .filter(Boolean)
        .join("&");

    if (!appliedFilters) return "";
    return isAdditionalFilters ? `?${appliedFilters}` : `&${appliedFilters}`;
};

export const dashboardApi = api.injectEndpoints({
    endpoints: (build) => ({
        getLazyPublicationCalendar: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                const searchParam = params.searchFields?.analyst_name
                    ? `&search=${params.searchFields.analyst_name}`
                    : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/research/publication-calendar/?export_to_excel=true&include_bespoke=true&start_date=${params.startDate}&end_date=${params.endDate}${pageParams}${pageSizeParams}${searchParam}${appliedFilters}`;
            },
        }),
        getLazyPublicationHeatMap: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/research/publication-heatmap/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}${appliedFilters}`;
            },
        }),
        getLazyPublicationCalendarHistory: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/research/publication-calendar/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}&history=true${pageParams}${pageSizeParams}${appliedFilters}`;
            },
        }),
        getLazyTargetMissed: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                return `/research/target-missed/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}${pageParams}${pageSizeParams}${appliedFilters}`;
            },
        }),
        getLazyWeeklySummary: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                return `/research/weekly-summary/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}${pageParams}${pageSizeParams}${appliedFilters}`;
            },
        }),
        getLazyTrackInitiations: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                return `/research/track-initiations/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}${pageParams}${pageSizeParams}${appliedFilters}`;
            },
        }),
        getLazyCoverageSpreads: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                return `/research/coverage-performance/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}${pageParams}${pageSizeParams}${appliedFilters}`;
            },
        }),
        getLazyPerformanceSectors: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/research/performance-sector/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}${appliedFilters}`;
            },
        }),
        getLazyLongNotesAnalysis: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                return `/research/long-notes-analysis/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}${pageParams}${pageSizeParams}${appliedFilters}`;
            },
        }),
        getLazyCorporatePlansSummary: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);

                return `/research/corporate-plans-summary/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}${appliedFilters}`;
            },
        }),
        getLazyTrendView: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);

                return `/research/trend/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}${appliedFilters}`;
            },
        }),
        getLazyHistoricalView: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);

                return `/research/historical/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}${appliedFilters}`;
            },
        }),
        getLazyTop25: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/research/top-25/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}${appliedFilters}`;
            },
        }),
        getLazyUpcomingPublicationSchedule: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/research/upcoming-publication-schedule/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}${appliedFilters}`;
            },
        }),
        getLazyCoverageUniverse: build.query({
            query: () => {
                return `/research/coverage-universe/`;
            },
        }),
        getLazyRatingSpread: build.query({
            query: () => {
                return `/research/rating-spread/`;
            },
        }),
        getLazyAnalystList: build.query({
            query: () => `/research/analysts/`,
            providesTags: ["AnalystList"],
        }),
        getLazyAnalystDetail: build.query({
            query: (params) => {
                return `/research/analysts/${params.id}/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}`;
            },
        }),
        getLazySectorPublications: build.query({
            query: () => {
                return `/research/sector-publications/`;
            },
        }),
        getLazyDynamicFilterOptions: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params, true);
                return `/research/dynamic-filter-options/${appliedFilters}`;
            },
        }),
        getLazyPublicationsExtract: build.query({
            query: (params) => {
                const pageParams = params.page ? `&page=${params.page}` : "";
                const appliedFilters = createQueryParams(params.appliedFilters);
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                return `/research/publication-reports/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}${pageParams}${pageSizeParams}${appliedFilters}`;
            },
        }),
        getLazyCorporateEngagementsCalendar: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/research/corporate-engagements-calendar/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}${appliedFilters}`;
            },
        }),
        getLazyAnalystsVotesSpread: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);
                return `/research/analyst-votes-spread/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}${appliedFilters}`;
            },
        }),
        getLazyCorporateTouchPoints: build.query({
            query: (params) => {
                return `/research/corporate-touch-points/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}`;
            },
        }),
        getLazySectorsVotesSpread: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);
                const pageParams = params.page ? `&page=${params.page}` : "";
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                return `/research/analyst-votes-spread/analyst/${params.appliedFilters.analyst_id}/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}${pageParams}${pageSizeParams}${appliedFilters}`;
            },
        }),
        getLazyAnalystServiceTouchPoints: build.query({
            query: (params) => {
                const appliedFilters = createQueryParams(params.appliedFilters);
                const pageParams = params.page ? `&page=${params.page}` : "";
                const pageSizeParams = params.pageSize
                    ? `&page_size=${params.pageSize}`
                    : "";
                return `/research/analyst-touchpoint-summary/analyst/${params.appliedFilters.analyst_id}/?export_to_excel=true&start_date=${params.startDate}&end_date=${params.endDate}${pageParams}${pageSizeParams}${appliedFilters}`;
            },
        }),
        getLazyCorporateAccessGroups: build.query({
            query: () => {
                return `/research/corporate-access-groups/`;
            },
        }),
    }),
    overrideExisting: false,
});

export const {
    useLazyGetLazyCorporateTouchPointsQuery,
    useLazyGetLazyPublicationCalendarQuery,
    useLazyGetLazyPublicationCalendarHistoryQuery,
    useLazyGetLazyTargetMissedQuery,
    useLazyGetLazyWeeklySummaryQuery,
    useLazyGetLazyTrackInitiationsQuery,
    useLazyGetLazyCoverageSpreadsQuery,
    useLazyGetLazyPerformanceSectorsQuery,
    useLazyGetLazyLongNotesAnalysisQuery,
    useLazyGetLazyPublicationHeatMapQuery,
    useLazyGetLazyCorporatePlansSummaryQuery,
    useLazyGetLazyUpcomingPublicationScheduleQuery,
    useLazyGetLazyCoverageUniverseQuery,
    useLazyGetLazyRatingSpreadQuery,
    useLazyGetLazyAnalystListQuery,
    useLazyGetLazyAnalystDetailQuery,
    useLazyGetLazySectorPublicationsQuery,
    useLazyGetLazyDynamicFilterOptionsQuery,
    useLazyGetLazyPublicationsExtractQuery,
    useLazyGetLazyAnalystsVotesSpreadQuery,
    useLazyGetLazySectorsVotesSpreadQuery,
    useLazyGetLazyCorporateEngagementsCalendarQuery,
    useLazyGetLazyAnalystServiceTouchPointsQuery,
    useLazyGetLazyCorporateAccessGroupsQuery,
    useLazyGetLazyTrendViewQuery,
    useLazyGetLazyHistoricalViewQuery,
    useLazyGetLazyTop25Query,
} = dashboardApi;
